import { FC, useMemo, useState } from "react";
import { getClients } from "@payping/redux/actions";
import { ensureString } from "@payping/utilities-shared/string";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";

import {
  Button,
  Col,
  Collapse,
  Descriptions,
  Drawer,
  Flex,
  Grid,
  Row,
  Skeleton,
  Space,
  Tag,
  Timeline,
  Typography,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { ClockCircleOutlined, CuToman } from "@payping/icons";
import { dateFormats, toJalali } from "@payping/utils/dateUtils";
import {
  PaymentDetailsVM,
  ServiceType,
  TransactionReportViewModel,
  getTransactionStatus,
  invoiceV2Apis,
  permalinkApis,
  reportApis,
  timelineApis,
  uploadV2Apis,
} from "@payping/api";
import { useAsync, useAsyncFn, useAsyncRetry } from "react-use";
import { ValidEmail, ValidMobileNumber } from "@payping/utils/validation";

import { useAppDispatch } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { getCorrectDate } from "@/utils/getCorrectDate";
import { useLastUrlWasPage } from "@/utils";
import {
  CreateRefundAction,
  // CreateRefundAction,
  RetryRefundAction,
} from "@/refund/components";
import { REFUND_STATE_COLOR_DICTIONARY } from "@/refund/constants/dictionary.constant";
import { useGetLatestRefundInfoByPaymentCode } from "@/refund/use-cases";
import { MIN_AMOUNT } from "@/refund/constants/general.constant";

interface CustomTransactionDetailsVM extends PaymentDetailsVM {
  bWage?: number;
  pWage?: number;
}

interface Props {
  code: string;
  data?: TransactionReportViewModel;
  open?: boolean;
  onClose: () => void;
}

export const TransactionDetails: FC<Props> = ({ code, onClose, open, data }) => {
  const dispatch = useAppDispatch();
  const { t } = useAppTranslation();
  // const details: CustomTransactionDetailsVM | undefined = useAppSelector((state) => state.reports.details);
  const [details, setDetails] = useState<CustomTransactionDetailsVM | undefined>();
  const payload = details?.amount && details?.amount >= MIN_AMOUNT ? code : "";
  const refundInfo = useGetLatestRefundInfoByPaymentCode(payload);

  const { md } = Grid.useBreakpoint();
  const { navigateToLast } = useLastUrlWasPage();

  const { value: clients } = useAsync(() => dispatch(getClients()), []);

  const { value: itemBuyer } = useAsync(async () => {
    if (details?.clientId !== "PaypingProduct") {
      return Promise.reject();
    }
    return permalinkApis.permaLinkGetBuyerDetailByPaymentCode(code, { isSilent: true });
  }, [code, details]);

  const { value: invoiceBuyer } = useAsync(async () => {
    const invoiceCode = details?.clientRefId;
    if (details?.clientId !== "PaypingInvoice" || !invoiceCode) {
      return Promise.reject();
    }
    return invoiceV2Apis.invoiceBuyer(invoiceCode, { isSilent: true });
  }, [details]);

  const { value: timeLine } = useAsync(() => timelineApis.v1TimeLineListGet(ServiceType.NUMBER_1, code), [code]);

  const { loading } = useAsyncRetry(async () => {
    try {
      const response = await reportApis.reportPaymentDetails(code);
      setDetails(response);
    } catch {
      navigateToLast();
    }
  }, [code]);

  // TODO: this is a temporary solution for users who have wage on payer side (they can't see payer details inside transaction details page), this should be fixed in new payment
  const [, handlePdf] = useMessagedAsyncFn(
    async (newWindow: Window | null) => {
      if (details?.clientRefId) {
        const path = await uploadV2Apis.productBuyersWithProductCodeGet(details?.clientRefId);
        newWindow?.location?.assign?.(path);
      }
    },
    [details?.clientRefId],
    undefined,
  );

  const [{ loading: getExportLoading }, getExport] = useAsyncFn(async () => {
    const url = await uploadV2Apis.paymentReportPdfWithPayCodeGet(code);
    return url;
  }, [code]);

  const {
    name: statusText,
    payMethod: ipgTypeText,
    color: apiColor,
  } = getTransactionStatus({
    isBlocked: details?.isBlocked,
    isPaid: details?.isPaid,
    isUnfetched: false,
    payMethod: details?.paymentType,
  });

  const { string: payDateFa } = toJalali({
    date: getCorrectDate(details?.payDate),
    toFormat: dateFormats.ui.jalaliDateTime,
  });

  const wage = useMemo(() => {
    if (!details?.bWage && !details?.pWage)
      return ensureString({ text: t("report.sidebar.zeroWage"), fallbackValue: "-" });
    if (details?.bWage)
      return `${formatLocalizedNumber({ value: details?.bWage })} ${t("toman")} ${t("report.sidebar.bWage")}`;
    if (details?.pWage)
      return `${formatLocalizedNumber({ value: details?.pWage })} ${t("toman")} ${t("report.sidebar.pWage")}`;
    return "";
  }, [details, t]);
  const shaparakWage = details?.shaparakWage ? formatLocalizedNumber({ value: details.shaparakWage }) : "-";

  const clientIdName = details?.isRequest
    ? t("report.tabs.requestMoney")
    : clients?.find(({ clientId }) => clientId === details?.clientId)?.displayName;

  const timeLineContent = (
    <Timeline>
      {timeLine?.map((item) => (
        <Timeline.Item key={item.code}>
          <div className="tw-pr-3">
            <p className={"color--gray500"}>
              {toJalali({ date: item.eventDate, toFormat: "HH:mm jYYYY/jMM/jDD" }).string}
              <ClockCircleOutlined width={12} className={"color--gray500 tw-mr-[5px] tw-mt-1"} />
            </p>
            <p className="tw--mt-3" style={{ color: "#202124" }}>
              {item.title}
            </p>
            <div
              className="tw-mb-[15px] tw-w-full tw-rounded-lg tw-p-[15px]"
              style={{
                backgroundColor: "#fff",
              }}
            >
              <span style={{ color: "#666666" }}>{item.description}</span>
            </div>
          </div>
        </Timeline.Item>
      ))}
    </Timeline>
  );

  const onPrintTransactionClick = async (newWindow: Window | null): Promise<void> => {
    const url = await getExport();
    newWindow?.location?.assign?.(url);
  };

  const printButton = (
    <Button
      onClick={() => {
        const newWindow = window.open(undefined, "_blank");
        onPrintTransactionClick(newWindow);
      }}
      loading={getExportLoading}
    >
      {t("report.print")}
    </Button>
  );

  const ribbonText = `${statusText}${ipgTypeText ? ` (${ipgTypeText})` : ""}`;

  return (
    <Drawer
      open={open}
      extra={printButton}
      title={t("report.transactionDetails")}
      placement={md ? "left" : "bottom"}
      onClose={onClose}
      height={"100%"}
      width={md ? "33rem" : "100%"}
    >
      <Row gutter={[16, 16]}>
        <Col span={24}>
          {loading ? (
            <Skeleton active paragraph />
          ) : (
            <Descriptions
              column={1}
              bordered
              title={
                <Flex vertical>
                  <Space className={"tw-flex tw-justify-between"}>
                    <Space direction="vertical">
                      {t("report.sidebar.paymentsDetails")}
                      <Flex align="center">
                        <Typography.Text>{`${formatLocalizedNumber({ value: details?.amount ?? 0 })}`}</Typography.Text>
                        <CuToman width={24} height={24} />
                      </Flex>
                    </Space>
                    <div className={"ltr"}>
                      <Typography.Text type={"secondary"}>
                        {ensureString({ text: payDateFa ?? "", fallbackValue: "-" })}
                      </Typography.Text>
                    </div>
                  </Space>
                  <Flex className="tw-mt-[16px]" gap="small">
                    <Button type="primary" disabled>
                      {t("report.sidebar.inquiryPayer")}
                    </Button>
                    <Button disabled>{t("report.sidebar.inquiryTransaction")}</Button>
                    <CreateRefundAction details={data} refundInfo={refundInfo.data} loading={refundInfo.isLoading} />
                    {/* <CancelRefundAction details={data} refundInfo={refundInfo.data} /> */}
                    <RetryRefundAction details={data} refundInfo={refundInfo.data} />
                  </Flex>
                </Flex>
              }
            >
              <Descriptions.Item label={t("report.sidebar.amount")}>
                <Flex align="center">
                  <Typography.Text type="secondary">{`${formatLocalizedNumber({ value: details?.amount ?? 0 })}`}</Typography.Text>
                  <CuToman width={24} height={24} />
                </Flex>
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.status")}>
                <Tag color={apiColor}>{ribbonText}</Tag>
              </Descriptions.Item>
              {!refundInfo.error && refundInfo.data && (
                <Descriptions.Item label={t("report.sidebar.refundStaus")}>
                  <Tag color={REFUND_STATE_COLOR_DICTIONARY[refundInfo.data?.state ?? "0"]}>
                    {refundInfo.data.localizedState}
                  </Tag>
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t("report.sidebar.invoiceNo")}>
                {ensureString({ text: details?.invoiceNo ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.code")}>
                {ensureString({ text: code ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.wage")}>{wage}</Descriptions.Item>
              {/* <Descriptions.Item label={t("report.sidebar.code")}>{ensureString({ text: code ?? "", fallbackValue: "-" })}</Descriptions.Item> */}
              <Descriptions.Item label={t("report.sidebar.name")}>
                {ensureString({ text: details?.name ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.rrn")}>
                <div className={"ltr"}>{ensureString({ text: details?.rrn ?? "", fallbackValue: "-" })}</div>
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.clientId")}>
                {ensureString({ text: clientIdName ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.description")}>
                {ensureString({ text: details?.description ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Col>
        <Col span={24}>
          {loading ? (
            <Skeleton active paragraph />
          ) : (
            <Descriptions column={1} bordered title={t("report.sidebar.paymentsDetails")}>
              <Descriptions.Item label={t("report.sidebar.amount")}>
                {formatLocalizedNumber({ value: details?.amount ?? 0 })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.payDate")}>
                <div className={"ltr"}>{ensureString({ text: payDateFa ?? "", fallbackValue: "-" })}</div>
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.invoiceNo")}>
                {ensureString({ text: details?.invoiceNo ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.wage")}>{wage}</Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.shaparakWage")}>
                {ensureString({ text: shaparakWage ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.couponName")}>
                {ensureString({ text: itemBuyer?.couponName ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.ipgName")}>
                {ensureString({ text: details?.ipgName ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Col>
        <Col span={24}>
          {loading ? (
            <Skeleton active paragraph />
          ) : (
            <Descriptions column={1} bordered title={t("report.sidebar.customer.detail")}>
              <Descriptions.Item label={t("report.sidebar.name")}>
                {ensureString({ text: details?.name ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.customer.phone")}>
                {ensureString({
                  text:
                    (itemBuyer || invoiceBuyer)?.phone ||
                    (ValidMobileNumber(details?.payerIdentity) ? details?.payerIdentity : "") ||
                    "",
                  fallbackValue: "-",
                })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.customer.email")}>
                {ensureString({
                  text:
                    (itemBuyer || invoiceBuyer)?.email ||
                    (ValidEmail(details?.payerIdentity) ? details?.payerIdentity : "") ||
                    "",
                  fallbackValue: "-",
                })}
              </Descriptions.Item>
              {invoiceBuyer ? <></> : null}
              {itemBuyer || invoiceBuyer ? (
                <>
                  <Descriptions.Item label={t("report.sidebar.invoiceBuyer.nationalId")}>
                    {ensureString({ text: invoiceBuyer?.nationalId ?? "", fallbackValue: "-" })}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("report.sidebar.customer.city")}>
                    {ensureString({ text: (itemBuyer || invoiceBuyer)?.city ?? "", fallbackValue: "-" })}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("report.sidebar.invoiceBuyer.customerCode")}>
                    {ensureString({ text: invoiceBuyer?.code ?? "", fallbackValue: "-" })}
                  </Descriptions.Item>
                </>
              ) : null}
              {itemBuyer ? (
                <Descriptions.Item label={t("report.sidebar.customer.customDescription")}>
                  {ensureString({ text: itemBuyer.customDescription ?? "", fallbackValue: "-" })}
                </Descriptions.Item>
              ) : null}

              {itemBuyer?.addressLine ? (
                <Descriptions.Item label={t("report.sidebar.customer.customerAddressLine")}>
                  {ensureString({ text: itemBuyer.addressLine ?? "", fallbackValue: "-" })}
                </Descriptions.Item>
              ) : null}
            </Descriptions>
          )}
        </Col>
        <Col span={24}>
          {loading && details?.clientId === "PaypingInvoice" ? (
            <Skeleton active paragraph />
          ) : (
            <Descriptions column={1} bordered title={t("report.sidebar.customer.serviceDetails")}>
              <Descriptions.Item label={t("report.sidebar.clientId")}>
                {ensureString({ text: clientIdName ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.invoiceBuyer.code")}>
                {details?.clientRefId}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.description")}>
                {ensureString({ text: details?.description ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Col>
        {itemBuyer?.productInfo ? (
          <Col span={24}>
            <Descriptions column={1} bordered title={t("report.sidebar.item.detail")}>
              <Descriptions.Item label={t("report.sidebar.item.code")}>{itemBuyer.productInfo.code}</Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.item.title")}>
                {itemBuyer.productInfo.title}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.item.quantity")}>{itemBuyer.quantity}</Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.item.itemPrice")}>
                {formatLocalizedNumber({ value: itemBuyer.productInfo.realAmount ?? 0 })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.description")}>
                {ensureString({ text: itemBuyer.productInfo.description ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
            </Descriptions>
          </Col>
        ) : null}

        {details?.clientId === "PaypingProduct" && !itemBuyer?.productInfo ? (
          <Col span={24}>
            <Descriptions column={1} bordered title={t("report.sidebar.item.detail")}>
              <Descriptions.Item label={t("report.title")}>
                <Typography.Link
                  onClick={() => {
                    const newWindow = window.open(undefined, "_blank");
                    handlePdf(newWindow);
                  }}
                >
                  {t("report.sidebar.downloadProductPdfReport")}
                </Typography.Link>
              </Descriptions.Item>
            </Descriptions>
          </Col>
        ) : null}
        <Col span={24}>
          <Collapse>
            <Collapse.Panel header={t("report.transactionTimeline")} key={0}>
              {timeLineContent}
            </Collapse.Panel>
          </Collapse>
        </Col>
      </Row>
    </Drawer>
  );
};

export default TransactionDetails;
